import React, {useEffect, useRef, useState} from 'react';
import {ArrowLeft, ArrowRight, Check, Headphones, X} from 'lucide-react';
import {WALKTHROUGH_STEPS} from '../core/walkthrough.js';
import {createWalkthroughNarrator, WALKTHROUGH_WELCOME, WALKTHROUGH_FINISH} from '../core/walkthroughAudio.js';
import {WalkthroughPreview} from './WalkthroughPreview.jsx';

export function Walkthrough({welcome = false, onClose, onDontShowAgain}) {
  const [page, setPage] = useState(welcome ? -1 : 0);
  const [narrationStatus, setNarrationStatus] = useState('Narration ready');
  const narratorRef = useRef(null);
  const initialPage = useRef(welcome ? -1 : 0);
  useEffect(() => {
    narratorRef.current = createWalkthroughNarrator(setNarrationStatus);
    narratorRef.current.select(initialPage.current);
    return () => { narratorRef.current?.dispose(); narratorRef.current = null; };
  }, []);
  function goTo(next, consent = false) {
    narratorRef.current?.select(next, consent);
    setPage(next);
  }
  function close() { narratorRef.current?.dispose(); onClose(); }
  function hide() { narratorRef.current?.dispose(); onDontShowAgain(); }
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);
  const finish = page === WALKTHROUGH_STEPS.length;

  useEffect(() => {
    returnFocusRef.current = document.activeElement;
    dialogRef.current?.querySelector('[data-walkthrough-primary]')?.focus();
    return () => returnFocusRef.current?.focus?.();
  }, []);
  useEffect(() => { dialogRef.current?.querySelector(page >= 0 && !finish ? '#walkthrough-title' : '[data-walkthrough-primary]')?.focus(); }, [page, finish]);
  useEffect(() => {
    const onKey = event => {
      if (event.key === 'Escape') { event.preventDefault(); close(); }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const controls = [...dialogRef.current.querySelectorAll('button:not([disabled])')];
      if (!controls.length) return;
      if (event.shiftKey && document.activeElement === controls[0]) { event.preventDefault(); controls.at(-1).focus(); }
      else if (!event.shiftKey && document.activeElement === controls.at(-1)) { event.preventDefault(); controls[0].focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const step = WALKTHROUGH_STEPS[page];
  return <div className="walkthrough-backdrop">
    <section className="walkthrough-dialog" role="dialog" aria-modal="true" aria-labelledby="walkthrough-title" aria-describedby="walkthrough-description" ref={dialogRef}>
      <button className="walkthrough-close" type="button" aria-label="Close walkthrough" onClick={close}><X size={20}/></button>
      {page === -1 ? <>
        <div className="walkthrough-mark"><Headphones size={34}/></div>
        <span className="walkthrough-eyebrow">A quick tour</span>
        <h2 id="walkthrough-title">Welcome to QLine The Game</h2>
        <div id="walkthrough-description" className="walkthrough-copy">{WALKTHROUGH_WELCOME.map(paragraph => <p key={paragraph}>{paragraph}</p>)}</div>
        <div className="walkthrough-actions"><button className="button primary" data-walkthrough-primary type="button" onClick={() => goTo(0, true)}>Show Me Around <ArrowRight size={18}/></button><button className="button secondary" type="button" onClick={close}>Skip Walkthrough</button><button className="walkthrough-text-button" type="button" onClick={hide}>Don’t Show This Again</button></div>
        <p className="walkthrough-replay-note">You can replay this walkthrough later from Help or Settings.</p>
      </> : finish ? <>
        <div className="walkthrough-mark"><Check size={34}/></div>
        <span className="walkthrough-eyebrow">Tour complete</span>
        <h2 id="walkthrough-title">And that’s QLine.</h2>
        <div id="walkthrough-description" className="walkthrough-copy">{WALKTHROUGH_FINISH.map(paragraph => <p key={paragraph}>{paragraph}</p>)}</div>
        <div className="walkthrough-actions"><button className="button primary" data-walkthrough-primary type="button" onClick={close}>Start Using QLine <ArrowRight size={18}/></button><button className="button secondary" type="button" onClick={() => goTo(0, true)}>Replay Walkthrough</button><button className="walkthrough-text-button" type="button" onClick={hide}>Don’t Show This Again</button></div>
      </> : <>
        <div className="walkthrough-progress"><span>Step {page + 1} of {WALKTHROUGH_STEPS.length}</span><span>{Math.round((page + 1) / WALKTHROUGH_STEPS.length * 100)}%</span></div>
        <div className="walkthrough-progress-track" aria-hidden="true"><i style={{width:`${(page + 1) / WALKTHROUGH_STEPS.length * 100}%`}}/></div>
        <h2 id="walkthrough-title" tabIndex={-1}>Step {page + 1}: {step.title}</h2>
        <div id="walkthrough-description" className="walkthrough-step-content">
          <WalkthroughPreview page={page} step={step}/>
          <div className="walkthrough-copy">{step.paragraphs.map((paragraph,index)=><p key={index}>{paragraph}</p>)}</div>
        </div>
        <div className="walkthrough-navigation"><button className="button secondary" type="button" onClick={() => goTo(page - 1)}><ArrowLeft size={17}/>Back</button><button className="button primary" data-walkthrough-primary type="button" onClick={() => goTo(page + 1)}>{page === WALKTHROUGH_STEPS.length - 1 ? 'Finish' : 'Next'}<ArrowRight size={17}/></button></div>
        <button className="walkthrough-text-button walkthrough-skip" type="button" onClick={close}>Skip Walkthrough</button>
      </>}
      <div className="walkthrough-narration" aria-label="Walkthrough narration">
        <button className="button secondary" type="button" onClick={() => narratorRef.current?.play()} aria-label="Play walkthrough narration">Play narration</button>
        <button className="button secondary" type="button" onClick={() => narratorRef.current?.pause()} aria-label="Pause walkthrough narration">Pause</button>
        <button className="button secondary" type="button" onClick={() => narratorRef.current?.play(true)} aria-label="Replay walkthrough narration">Replay narration</button>
        <p role="status" aria-live="polite">{narrationStatus}</p>
      </div>
    </section>
  </div>;
}
